/**
 * Where to send someone after they sign in, and only ever somewhere of ours.
 *
 * A link out of an Ivanti notification email (`/t/12345`) reaches a signed-out
 * reader first; `ProtectedRoute` sends them to `/login?next=/t/12345`, and the
 * login page hands that on to the SSO round trip. Because `next` arrives in the
 * query string, anyone can write it — so it is an open redirect unless every
 * value is checked to be a same-origin relative path. The backend validates it
 * again before redirecting after the callback; this is the client's half.
 *
 * Deliberately import-free and erasable-syntax TypeScript, so
 * `tests/safe-return-path.test.mjs` can import it with Node's type stripping.
 */

/** Longer than any path this app produces; anything bigger is not a real link. */
export const RETURN_PATH_MAX_LENGTH = 2048;

/** Where a sign-in lands when there is no usable `next`. */
export const DEFAULT_RETURN_PATH = "/home";

/** A throwaway origin to resolve against; only "did the origin change" matters. */
const PROBE_ORIGIN = "http://return-path.invalid";

/** C0 controls and DEL. The URL parser silently strips tab/CR/LF, so `/\t/x` is `//x`. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/** Decoding rounds to check: enough for double-encoding, bounded so it terminates. */
const DECODE_ROUNDS = 3;

/** True when `value` looks like a protocol-relative or backslash-escaped start. */
function hasUnsafeStart(value: string): boolean {
  return value.startsWith("//") || value.startsWith("/\\") || value.startsWith("\\");
}

/** True when any percent-decoding of `raw` hides a scheme-relative start or a control char. */
function hidesUnsafeForm(raw: string): boolean {
  let current = raw;
  for (let round = 0; round < DECODE_ROUNDS; round += 1) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      // Malformed escapes: not something this app ever generates.
      return true;
    }
    if (decoded === current) {
      return false;
    }
    if (hasUnsafeStart(decoded) || CONTROL_CHARS.test(decoded) || decoded.includes("\\")) {
      return true;
    }
    current = decoded;
  }
  return false;
}

/** The login page itself (a loop) and the API (a redirect there is never a page). */
function isExcludedPath(pathname: string): boolean {
  // React Router matches case-insensitively, so `/LOGIN` is the login page too.
  const lower = pathname.toLowerCase();
  return (
    lower === "/login" ||
    lower.startsWith("/login/") ||
    lower === "/api" ||
    lower.startsWith("/api/")
  );
}

/**
 * `raw` if it is a safe same-origin relative path, normalised; otherwise `fallback`.
 *
 * Accepts `/path?query#hash`. Rejects: anything not starting with exactly one
 * `/` (`//evil.com`, `/\evil.com`, `https:…`, `javascript:…`, leading
 * whitespace), control characters anywhere, backslashes, percent-encoded forms
 * of the same (`/%2F%2Fevil.com`), `/login` (it would loop) and `/api/*`, and
 * anything longer than {@link RETURN_PATH_MAX_LENGTH}.
 */
export function safeReturnPath(
  raw: string | null | undefined,
  fallback = DEFAULT_RETURN_PATH,
): string {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > RETURN_PATH_MAX_LENGTH) {
    return fallback;
  }
  if (!raw.startsWith("/") || hasUnsafeStart(raw)) {
    return fallback;
  }
  if (CONTROL_CHARS.test(raw) || raw.includes("\\") || hidesUnsafeForm(raw)) {
    return fallback;
  }

  let url: URL;
  try {
    url = new URL(raw, PROBE_ORIGIN);
  } catch {
    return fallback;
  }
  if (url.origin !== PROBE_ORIGIN) {
    return fallback;
  }
  // The parser has resolved dot segments, so `/a/../login` is caught here too.
  if (hasUnsafeStart(url.pathname) || isExcludedPath(url.pathname)) {
    return fallback;
  }

  const normalised = `${url.pathname}${url.search}${url.hash}`;
  return normalised.length > RETURN_PATH_MAX_LENGTH ? fallback : normalised;
}

/**
 * The login URL for a signed-out visitor who asked for `current`.
 *
 * `current` is `pathname + search + hash`. The root and the login page carry no
 * `next` — the default landing is already right for them — and neither does
 * anything `safeReturnPath` would refuse on the way back.
 */
export function loginPathFor(current: string): string {
  if (current === "" || current === "/" || current.startsWith("/?") || current.startsWith("/#")) {
    return "/login";
  }
  const safe = safeReturnPath(current, "");
  return safe === "" ? "/login" : `/login?next=${encodeURIComponent(safe)}`;
}
